import { keoChap } from "../../../helpers/dieuKhoanTran";
import moment from "moment";

import * as SBService from "../../../helpers/sportbookService";
import { asArray, dateRange, shortName, unwrap } from "../shared/format";
import {
  SwissLeagueInfo,
  SwissMe,
  SwissPlayoff,
  SwissPrize,
  SwissRound,
  SwissStandings,
  SwissVenue,
  SwissView,
} from "./types";

/** Người chưa đăng nhập vẫn xem được giải, chỉ mất phần "của bạn". */
const EMPTY_ME: SwissMe = {
  registered: false,
  avatar: null,
  rankClass: null,
  score: null,
  state: "not_joined",
  requiresFee: false,
  feePaid: false,
  fee: null,
  venueId: null,
  isCheckin: false,
  wins: 0,
  losses: 0,
  byes: 0,
  lives: 0,
  rank: null,
  eliminatedRound: null,
  aliveCount: 0,
  totalPlayers: 0,
  currentRound: 0,
  closedRounds: 0,
  currentMatch: null,
  playoff: null,
  playerId: null,
};

const EMPTY_PLAYOFF: SwissPlayoff = {
  enabled: false,
  drawn: false,
  size: 0,
  rounds: 0,
  race: 0,
  roundList: [],
};


/** "Q.1" từ "Biadi Arena Quận 1" - nhãn ngắn cho chip địa điểm. */

/**
 * Backend bọc mọi phản hồi trong { data: ... } (ResponseTransformerInterceptor),
 * đúng như các màn khác vẫn phải bóc (`rsp.data ? rsp.data : rsp`). Không bóc thì
 * rounds là object chứ không phải mảng, .map ném TypeError và cả trang báo "Không
 * tải được giải đấu" — lỗi không kèm mã HTTP vì nó là lỗi JS.
 */

/** Dữ liệu hỏng hoặc thiếu thì coi như rỗng, đừng để .map ném lỗi. */

const mapVenues = (venues: any[]): SwissVenue[] =>
  asArray(venues).map((venue) => ({
    id: venue.id,
    name: venue.name || "",
    short: shortName(venue.name || ""),
    addr: venue.address || "",
    tables: venue.tableCount ?? null,
    cap: venue.capacity ?? null,
    taken: venue.taken ?? 0,
  }));

const mapPrizes = (prizes: any[]): SwissPrize[] =>
  asArray(prizes).map((prize) => ({
    label: prize.label || "",
    cash: prize.prizeInCash ?? null,
    kind: prize.prizeInKind ?? null,
  }));

const mapLeague = (overview: any): SwissLeagueInfo => {
  const league = overview?.league || {};
  return {
    id: league.id,
    name: league.name || "",
    format: "Swiss",
    /* Cách giải kết thúc, do ban tổ chức khai lúc tạo giải. Chỉ nhận đúng hai giá trị:
       một chuỗi lạ (API cũ, dữ liệu sửa tay) không được phép thành một chế độ thứ ba,
       và mặc định phải là "het-mang" vì đó là hành vi của mọi giải trước khi có cờ. */
    playoffMode: league.playoffMode === "cat-top" ? "cat-top" : "het-mang",
    lives: league.lives ?? 3,
    race: league.race ?? 5,
    est: league.estimatedRounds ?? null,
    players: league.numberParticipants ?? 0,
    fee: league.fee ?? null,
    feeQrImage: league.feeQrImage ?? null,
    feeTransferNote: league.feeTransferNote ?? null,
    feeDeadlineDate: league.feeDeadlineDate ?? null,
    rule: league.rule ?? null,
    logo: league.logo ?? null,
    banner: league.banner ?? null,
    reg: dateRange(league.startRegistrationDate, league.endRegistrationDate),
    play: dateRange(league.startDate, league.endDate),
    startRegDate: league.startRegistrationDate ?? null,
    endRegDate: league.endRegistrationDate ?? null,
    startPairingDate: league.startPairingDate ?? null,
    startDate: league.startDate ?? null,
    endDate: league.endDate ?? null,
    currentRound: league.currentRound ?? 0,
    openRound: league.openRound ?? null,
    closedRounds: league.closedRounds || [],
    isFinished: !!league.isFinished,
    hasFee: !!league.hasFee,
    registered: overview?.totals?.registered ?? 0,
    official: overview?.totals?.official ?? overview?.totals?.registered ?? 0,
    pendingFee: overview?.totals?.pendingFee ?? 0,
    alive: overview?.totals?.alive ?? 0,
  };
};

const mapMatch = (match: any) => ({
  scheduleId: match.scheduleId,
  matchId: match.matchId ?? null,
  round: match.round,
  status: match.status,
  isBye: !!match.isBye,
  mine: !!match.mine,
  table: match.table ?? null,
  venueId: match.venueId ?? null,
  venueName: match.venueName ?? null,
  race: match.race ?? 0,
  players: asArray(match.players).map((player: any) => ({
    id: player.playerId ?? null,
    name: player.name || "",
    score: player.score ?? null,
    isWinner: !!player.isWinner,
  })),
  // Dựng ở đây vì chỉ ở đây còn biết ghế nào là người thách - `players` bên dưới đã
  // sắp theo thứ tự hiển thị, không phải thứ tự thách/nhận.
  keo: keoChap(match, tenGhe(match, match.challengerId), tenGhe(match, match.acceptorId)),
});

/** Tên của một ghế theo id, hoặc chuỗi rỗng khi cặp chưa đủ người. */
const tenGhe = (match: any, playerId: string | null | undefined): string =>
  asArray(match.players).find((player: any) => player.playerId === playerId)?.name || "";

const mapRounds = (rounds: any[]): SwissRound[] =>
  asArray(rounds).map((round) => ({
    round: round.round,
    isOpen: !!round.isOpen,
    matches: asArray(round.matches).map(mapMatch),
  }));

const mapPlayoff = (playoff: any): SwissPlayoff => {
  if (!playoff) {
    return EMPTY_PLAYOFF;
  }
  return {
    enabled: !!playoff.enabled,
    drawn: !!playoff.drawn,
    size: playoff.size ?? 0,
    rounds: playoff.rounds ?? 0,
    race: playoff.race ?? 0,
    roundList: asArray(playoff.roundList).map((round: any) => ({
      round: round.round,
      name: round.name || "",
      matches: asArray(round.matches).map(mapMatch),
    })),
  };
};

const mapStandings = (standings: any): SwissStandings => ({
  round: standings?.round ?? 0,
  isSnapshot: !!standings?.isSnapshot,
  isFinal: !!standings?.isFinal,
  rows: asArray(standings?.rows).map((row: any) => ({
    position: row.position,
    groupPosition: row.groupPosition ?? row.position,
    avatar: row.avatar ?? null,
    rankClass: row.rankClass ?? null,
    rating: row.rating ?? null,
    playerId: row.playerId ?? null,
    name: row.name || "",
    wins: row.wins ?? 0,
    losses: row.losses ?? 0,
    byes: row.byes ?? 0,
    lives: row.lives ?? 0,
    sos: row.sos ?? 0,
    eliminatedRound: row.eliminatedRound ?? null,
    playoffNote: row.playoffNote ?? null,
  })),
});

const mapMe = (me: any, playerId: string | null): SwissMe => {
  if (!me) {
    return EMPTY_ME;
  }
  return {
    registered: !!me.registered,
    avatar: me.avatar ?? null,
    rankClass: me.rankClass ?? null,
    score: me.score ?? null,
    state: me.state,
    requiresFee: !!me.requiresFee,
    feePaid: !!me.feePaid,
    fee: me.fee ?? null,
    feeDeadline: me.feeDeadline ?? null,
    billUploaded: !!me.billUploaded,
    billImage: me.billImage ?? null,
    venueId: me.venueId ?? null,
    isCheckin: !!me.isCheckin,
    wins: me.wins ?? 0,
    losses: me.losses ?? 0,
    byes: me.byes ?? 0,
    lives: me.lives ?? 0,
    rank: me.rank ?? null,
    eliminatedRound: me.eliminatedRound ?? null,
    aliveCount: me.aliveCount ?? 0,
    totalPlayers: me.totalPlayers ?? 0,
    currentRound: me.currentRound ?? 0,
    closedRounds: me.closedRounds ?? 0,
    currentMatch: me.currentMatch ?? null,
    playoff: me.playoff ?? null,
    playerId,
  };
};

/**
 * Tải toàn bộ dữ liệu một giải Swiss.
 *
 * Đã đăng nhập thì gọi bản có token để lấy kèm trạng thái của mình; chưa đăng
 * nhập thì rơi về bản công khai và trang chỉ hiển thị phần chung.
 */
export const fetchSwissView = async (
  leagueId: string,
  playerId: string | null
): Promise<SwissView> => {
  const authed = !!playerId;

  // Chỉ overview là bắt buộc: thiếu nó thì không có gì để hiển thị. Ba phần còn
  // lại hỏng thì trang vẫn phải mở được - trước đây một lỗi lẻ ở /me, /rounds
  // hay bảng xếp hạng là cả trang báo "Không tìm thấy giải Swiss này".
  const soft = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => fallback);

  // Bản có token hỏng thì rơi về bản công khai: người xem mất phần "của bạn"
  // nhưng vẫn thấy giải, thay vì cả trang thành "Không tìm thấy giải Swiss này".
  const overviewOf = async () => {
    if (!authed) {
      return await SBService.getPublicSwissOverview(leagueId);
    }
    try {
      return await SBService.getSwissOverview(leagueId);
    } catch (e) {
      return await SBService.getPublicSwissOverview(leagueId);
    }
  };

  const roundsOf = async () => {
    if (!authed) {
      return await soft(SBService.getPublicSwissRounds(leagueId), []);
    }
    try {
      return await SBService.getSwissRounds(leagueId);
    } catch (e) {
      return await soft(SBService.getPublicSwissRounds(leagueId), []);
    }
  };

  /* Sơ đồ nhánh: giải không cắt top thì endpoint vẫn trả về khối rỗng, nên gọi
     luôn thay vì phải biết trước giải có nhánh hay không. */
  const playoffOf = async () => {
    if (!authed) {
      return await soft(SBService.getPublicSwissPlayoff(leagueId), null);
    }
    try {
      return await SBService.getSwissPlayoff(leagueId);
    } catch (e) {
      return await soft(SBService.getPublicSwissPlayoff(leagueId), null);
    }
  };

  const [overviewRsp, roundsRsp, playoffRsp, standingsRsp, meRsp] = await Promise.all([
    overviewOf(),
    roundsOf(),
    playoffOf(),
    soft(SBService.getSwissStandings(leagueId), null),
    authed ? soft(SBService.getSwissMyStatus(leagueId), null) : Promise.resolve(null),
  ]);

  const overview = unwrap(overviewRsp);
  const rounds = unwrap(roundsRsp);
  const playoff = unwrap(playoffRsp);
  const standings = unwrap(standingsRsp);
  const me = unwrap(meRsp);

  const view: SwissView = {
    league: mapLeague(overview),
    venues: mapVenues(overview?.venues),
    prizes: mapPrizes(overview?.prizes),
    rounds: mapRounds(rounds),
    playoff: mapPlayoff(playoff),
    standings: mapStandings(standings),
    me: mapMe(me, playerId),
  };

  // Overview biết đăng ký của tôi kể cả khi /me chưa kịp trả về địa điểm.
  if (!view.me.venueId && overview?.myRegistration?.venueId) {
    view.me.venueId = overview.myRegistration.venueId;
  }

  /* GIẢI KHÔNG CÓ VÒNG LOẠI TRỰC TIẾP THÌ XOÁ SẠCH PHẦN NHÁNH, MỘT CHỖ DUY NHẤT.
     Trang đọc `playoff` và `me.playoff` ở hơn hai chục chỗ - thẻ trạng thái, dòng tóm
     tắt, tab Thi đấu, tab BXH, sơ đồ. Rắc một điều kiện vào từng chỗ là cách chắc chắn
     nhất để bỏ sót một chỗ, mà sót ở đây nghĩa là một giải đánh tới khi còn một người
     bỗng hiện chữ "vòng loại trực tiếp".

     `|| drawn`: giải ĐÃ bốc nhánh thì phần nhánh ở lại, dù cờ nói "het-mang". Giải cắt
     top từ trước khi có cờ này không có khoá nào trong sub_data nên đọc ra "het-mang" -
     xoá nhánh của chúng là làm mất một sơ đồ đang chạy trên app của cơ thủ. Cùng một
     luật với SwissPlayoff::stageTwoApplies() bên back office. */
  if (view.league.playoffMode !== "cat-top" && !view.playoff.drawn) {
    view.playoff = EMPTY_PLAYOFF;
    view.me.playoff = null;
  }

  return view;
};
